import { useState, useRef, useEffect } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { personApi, LegalPersonSearchResult } from '../../../../../services/personApi';

const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

export type LegalPersonResult = LegalPersonSearchResult;

interface AssignorSearchProps {
    selectedPerson: LegalPersonResult | null;
    onSelect: (person: LegalPersonResult) => void;
    onClear: () => void;
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function AssignorSearch({ selectedPerson, onSelect, onClear }: AssignorSearchProps) {
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<LegalPersonResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchMessage, setSearchMessage] = useState('Buscando...');
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    // Cleanup on unmount: cancel pending debounce and abort in-flight request
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    const handleSearch = (value: string) => {
        setSearch(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (abortRef.current) abortRef.current.abort();

        if (value.length < MIN_SEARCH_LENGTH) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        setSearchMessage('Buscando...');
        debounceRef.current = setTimeout(async () => {
            abortRef.current = new AbortController();
            const maxRetries = 10;

            for (let attempt = 0; attempt <= maxRetries; attempt++) {
                try {
                    const data = await personApi.searchLegalPersons(value, abortRef.current.signal);
                    setResults(data);
                    break;
                } catch (e: any) {
                    if (e.name === 'AbortError') return;
                    if (e.message?.includes('504') && attempt < maxRetries) {
                        setSearchMessage('A busca pode demorar um pouco...');
                        continue;
                    }
                    toast.error(e.message || 'Erro ao buscar cedente');
                    break;
                }
            }
            setIsSearching(false);
            setSearchMessage('Buscando...');
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleSelect = (person: LegalPersonResult) => {
        onSelect(person);
        setSearch('');
        setResults([]);
    };

    if (selectedPerson) {
        return (
            <div className="flex items-center gap-3 mb-4">
                <span className="text-lg font-semibold dark:text-gray-100">Cedente</span>
                <div className="flex-1 flex items-center gap-3 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded">
                    <span className="text-sm font-medium dark:text-gray-100">
                        {selectedPerson.govTaxIdFormatted ? `${selectedPerson.govTaxIdFormatted} - ${selectedPerson.legalName}` : selectedPerson.legalName}
                        {selectedPerson.fantasyName && <span className="text-gray-500 dark:text-gray-400"> ({selectedPerson.fantasyName})</span>}
                    </span>
                    <button
                        onClick={onClear}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        title="Remover cedente"
                    >
                        <FaTimes />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-3 mb-4">
            <span className="text-lg font-semibold dark:text-gray-100">Cedente</span>
            <div className="flex-1 relative">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Buscar cedente por nome ou CNPJ (mín. 3 caracteres)"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-800 dark:text-gray-100"
                    />
                </div>
                {(isSearching || results.length > 0) && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded shadow-lg max-h-64 overflow-y-auto">
                        {isSearching ? (
                            <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                {searchMessage}
                            </div>
                        ) : (
                            results.map((person) => (
                                <button
                                    key={person.personId}
                                    onClick={() => handleSelect(person)}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 text-sm dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                                >
                                    <span className="font-medium">{person.govTaxIdFormatted}</span>
                                    {person.govTaxIdFormatted && ' - '}
                                    {person.legalName}
                                    {person.fantasyName && <span className="text-gray-500 dark:text-gray-400"> ({person.fantasyName})</span>}
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
